/**
 * Word Fluency Tap round-building helpers (target selection and distractor filtering).
 * Category tags are defined in the content bank manifest — no runtime tag patching.
 */

/** Broad categories safe for lower difficulty levels (levels 1–4). */
export const WORD_FLUENCY_BROAD_TARGET_CATEGORIES = new Set([
  "animals",
  "food",
  "vehicles",
  "nature",
  "household",
  "clothing",
  "sports",
  "tools",
]);

/** Tiny or mixed-domain categories — avoid as round targets when alternatives exist. */
export const WORD_FLUENCY_DEPRIORITIZED_TARGET_CATEGORIES = new Set([
  "things_that_move_slowly",
  "things_that_are_purple",
  "things_that_are_brown",
  "fast_food",
  "seafood",
  "meat",
  "nocturnal",
  "things_that_give_light",
]);

/** Nature subcategories — distractors sharing `nature` without the target tag are confusing. */
export const WORD_FLUENCY_NATURE_CLUSTER = new Set([
  "nature",
  "weather",
  "sky",
  "plants",
  "landscapes",
  "water",
  "winter",
  "cold_things",
  "things_that_are_hot",
  "autumn",
  "things_in_a_forest",
  "things_that_are_white",
  "things_that_are_colourful",
]);

/** Water-related categories with similar cross-tag confusion. */
export const WORD_FLUENCY_WATER_CLUSTER = new Set([
  "things_in_water",
  "sea_creatures",
  "water",
  "water_sports",
]);

export function isConfusingWordFluencyDistractor(
  categories: readonly string[],
  targetCategory: string,
): boolean {
  if (categories.includes(targetCategory)) {
    return false;
  }
  if (
    WORD_FLUENCY_NATURE_CLUSTER.has(targetCategory) &&
    categories.includes("nature")
  ) {
    return true;
  }
  if (
    WORD_FLUENCY_WATER_CLUSTER.has(targetCategory) &&
    categories.some((c) => WORD_FLUENCY_WATER_CLUSTER.has(c))
  ) {
    return true;
  }
  return false;
}

export function filterEligibleTargetCategories(
  categoriesInPool: string[],
  level: number,
): string[] {
  const unique = Array.from(new Set(categoriesInPool));
  let eligible = unique.filter(
    (c) => !WORD_FLUENCY_DEPRIORITIZED_TARGET_CATEGORIES.has(c),
  );
  if (eligible.length === 0) {
    eligible = unique;
  }
  if (level <= 4) {
    const broad = eligible.filter((c) =>
      WORD_FLUENCY_BROAD_TARGET_CATEGORIES.has(c),
    );
    if (broad.length > 0) {
      return broad;
    }
  }
  return eligible;
}
